import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { Capacitor } from '@capacitor/core';

import { DateTime } from 'luxon';

import {
  LocationContext,
  OptimizationReport,
  Suggestion
} from 'src/models/listing-optimizer.model';
import { OptimizerRun } from 'src/models/optimizer-run.model';
import { ListingOptimizerService } from 'src/shared/services/listing-optimizer.service';
import { Util } from 'src/shared/util/util';
import {
  SuggestionGroupVM,
  buildAmenityNames,
  buildSuggestionGroups,
  buildSummaryLine,
  isGeneratable,
  suggestionCardLabel
} from 'src/shared/components/onboarding/listing-optimization-onboarding/listing-optimizer-view-model';

interface LightboxState {
  originalUrl: string;
  enhancedUrl: string | null;
  view: 'before' | 'after';
}

interface ReportListingInfo {
  platform?: string;
  platform_id?: string | number;
}

interface RunDetailRow {
  label: string;
  value: string;
}

const PLATFORM_BADGES: Record<string, { label: string; icon: string | null }> = {
  airbnb: { label: 'Airbnb', icon: 'assets/img/airbnb-logo.svg' },
  vrbo: { label: 'VRBO', icon: null },
  booking_com: { label: 'Booking.com', icon: 'assets/img/integration-images/booking-channel-green.svg' },
};

@Component({
  selector: 'listing-optimizer-report',
  templateUrl: './listing-optimizer-report.component.html',
  styleUrls: ['./listing-optimizer-report.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ListingOptimizerReportComponent implements OnChanges {
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private listingOptimizerService = inject(ListingOptimizerService);

  @Input() report: OptimizationReport | null = null;
  @Input() showHeader = true;
  @Input() historyMode = false;
  /** Show the per-suggestion checkboxes + sticky "Have TIDY do these" bar. */
  @Input() showBulkActions = true;
  /** The connected AddressListing, when known — drives the platform badge line. */
  @Input() listing: ReportListingInfo | null = null;
  /** The persisted optimizer run, when known — drives the "View details" section. */
  @Input() run: OptimizerRun | null = null;
  @Output() reportExpired = new EventEmitter<void>();

  groups: SuggestionGroupVM[] = [];
  cardLabels: Record<string, string> = {};
  amenityNames: string[] = [];
  nearby: LocationContext | null = null;
  summaryLine = '';
  warnings: string[] = [];
  hasV2Content = false;

  platformLabel = '';
  platformIcon: string | null = null;
  platformListingId = '';

  detailsOpen = false;
  runDetailRows: RunDetailRow[] = [];
  runFailureMessage = '';
  runAppliedChanges: Array<{ field: string; before: string; after: string }> = [];

  canGenerate: Record<string, boolean> = {};
  genActions: Record<string, () => Promise<void>> = {};
  genError: Record<string, string> = {};
  private gen503: Record<string, boolean> = {};

  private selectableIds: string[] = [];
  selected: Record<string, boolean> = {};
  selectedCount = 0;
  allSelected = false;

  lightbox: LightboxState | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['listing'] || changes['report']) {
      this.buildPlatformBadge();
    }
    if (changes['run']) {
      this.buildRunDetails();
    }
    if (!changes['report']) {return;}
    this.gen503 = {};
    this.genError = {};
    this.normalizeReport();
    this.resetSelection();
    this.buildViewModel();
  }

  private resetSelection(): void {
    const ids = this.getRenderableSuggestions()
      .filter(suggestion => suggestion.group !== 'amenities')
      .map(suggestion => suggestion.id);
    this.selectableIds = ids;
    this.selected = Object.fromEntries(ids.map(id => [id, false]));
    this.selectedCount = 0;
    this.allSelected = false;
  }

  toggleSelect = (id: string): void => {
    this.selected = { ...this.selected, [id]: !this.selected[id] };
    this.recomputeSelection();
    this.cdr.markForCheck();
  };

  toggleSelectAll = (): void => {
    const next = !this.allSelected;
    this.selected = Object.fromEntries(this.selectableIds.map(id => [id, next]));
    this.selectedCount = next ? this.selectableIds.length : 0;
    this.allSelected = next;
    this.cdr.markForCheck();
  };

  private recomputeSelection(): void {
    this.selectedCount = this.selectableIds.reduce((count, id) => count + (this.selected[id] ? 1 : 0), 0);
    this.allSelected = this.selectableIds.length > 0 && this.selectedCount === this.selectableIds.length;
  }

  private normalizeReport(): void {
    if (!this.report) {return;}
    const abs = (url?: string) => this.listingOptimizerService.absoluteUrl(url);
    for (const suggestion of this.getRenderableSuggestions()) {
      // Reports round-tripped through our backend (persisted runs) can come
      // back without afterStatus (or snake_cased). Restore it so history
      // reports render with the same Generate / "Have TIDY do this" actions
      // as a freshly generated report.
      const raw = suggestion as any;
      if (!suggestion.afterStatus && raw.after_status) {
        suggestion.afterStatus = raw.after_status;
      }
      if (!suggestion.afterStatus) {
        suggestion.afterStatus = suggestion.after ? 'ready' : 'pending';
      }
      if (suggestion.before?.type === 'photo') {
        suggestion.before.url = abs(suggestion.before.url);
        if (suggestion.before.thumbnailUrl) {
          suggestion.before.thumbnailUrl = abs(suggestion.before.thumbnailUrl);
        }
      }
      if (suggestion.after?.type === 'photo') {
        suggestion.after.url = abs(suggestion.after.url);
        if (suggestion.after.thumbnailUrl) {
          suggestion.after.thumbnailUrl = abs(suggestion.after.thumbnailUrl);
        }
      }
    }
    for (const photo of this.report.listing?.photos || []) {
      if (photo.url) {photo.url = abs(photo.url);}
    }
  }

  // Reports only exist for connected listings; chat-api only analyzes Airbnb today,
  // so a missing listing input still implies Airbnb rather than an unknown platform.
  private buildPlatformBadge(): void {
    const platform = this.listing?.platform || (this.report ? 'airbnb' : '');
    const badge = platform ? (PLATFORM_BADGES[platform] || { label: platform, icon: null }) : null;
    this.platformLabel = badge?.label || '';
    this.platformIcon = badge?.icon || null;
    const listingId = this.listing?.platform_id ?? this.report?.listing?.listingId;
    this.platformListingId = listingId !== undefined && listingId !== null ? String(listingId) : '';
  }

  private buildRunDetails(): void {
    this.detailsOpen = false;
    const run = this.run;
    if (!run) {
      this.runDetailRows = [];
      this.runFailureMessage = '';
      this.runAppliedChanges = [];
      return;
    }
    const metadata = run.run_metadata || {};
    const jobId = [
      run.external_job_id, run.externalJobId, run.job_id, run.jobId, run.chat_job_id,
      run.listing_optimizer_job_id, metadata['external_job_id'], metadata['job_id'], metadata['jobId'],
      metadata['chat_job_id'], metadata['chatJobId'], metadata['listing_optimizer_job_id']
    ].find(candidate => typeof candidate === 'string' && candidate.length > 0) as string | undefined;
    const listingId = metadata['listing_id'] ?? this.listing?.platform_id;
    this.runDetailRows = [
      { label: 'Run ID', value: run.id > 0 ? String(run.id) : '—' },
      { label: 'Chat Job ID', value: jobId || '—' },
      { label: 'Listing ID', value: listingId !== undefined && listingId !== null ? String(listingId) : '—' },
      { label: 'Trigger', value: this.formatTrigger(run.trigger) },
      { label: 'Duration', value: this.formatDuration(run.duration) },
      { label: 'Started', value: this.formatRunDate(run.started_at || run.created_at) },
      { label: 'Completed', value: this.formatRunDate(run.completed_at) },
    ];
    this.runFailureMessage = run.status === 'failure' ? (run.failure_info?.message || '') : '';
    this.runAppliedChanges = Object.entries(run.applied_changes || {}).map(([field, diff]) => ({
      field,
      before: this.stringifyValue(diff?.before),
      after: this.stringifyValue(diff?.after),
    }));
  }

  toggleDetails = (): void => {
    this.detailsOpen = !this.detailsOpen;
    this.cdr.markForCheck();
  };

  private formatTrigger(trigger: string): string {
    const map: Record<string, string> = { manual: 'Manual', scheduled: 'Scheduled', api: 'API' };
    return map[trigger] ?? (trigger || '—');
  }

  private formatDuration(seconds: number | null): string {
    if (seconds === null || seconds === undefined) {return '—';}
    if (seconds < 60) {return `${seconds.toFixed(1)}s`;}
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  }

  private formatRunDate(dateStr: string | null | undefined): string {
    if (!dateStr) {return '—';}
    return DateTime.fromISO(dateStr).toFormat('MMM d, yyyy \'at\' h:mm a');
  }

  private stringifyValue(value: unknown): string {
    if (value === null || value === undefined) {return '—';}
    if (typeof value === 'string') {return value;}
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private buildViewModel(): void {
    const report = this.report;
    if (!report) {
      this.groups = [];
      this.cardLabels = {};
      this.amenityNames = [];
      this.nearby = null;
      this.summaryLine = '';
      this.warnings = [];
      this.canGenerate = {};
      this.genActions = {};
      this.hasV2Content = false;
      return;
    }

    const isV2Report = Array.isArray(report.suggestions);
    const suggestions = this.getRenderableSuggestions();
    const nonAmenity = suggestions.filter(suggestion => suggestion.group !== 'amenities');
    const listingId = report.listing?.listingId;
    const absoluteUrl = (url?: string) => this.listingOptimizerService.absoluteUrl(url);

    this.amenityNames = buildAmenityNames(suggestions);
    this.groups = buildSuggestionGroups(suggestions, report.listing?.photos || [], absoluteUrl);
    this.cardLabels = Object.fromEntries(nonAmenity.map(suggestion => [suggestion.id, suggestionCardLabel(suggestion)]));
    this.hasV2Content = isV2Report;

    const canGenerate: Record<string, boolean> = {};
    const genActions: Record<string, () => Promise<void>> = {};
    for (const suggestion of nonAmenity) {
      canGenerate[suggestion.id] = !this.historyMode && isGeneratable(suggestion) && !!listingId && !this.gen503[suggestion.id];
      genActions[suggestion.id] = () => this.generate(suggestion);
    }
    this.canGenerate = canGenerate;
    this.genActions = genActions;

    this.nearby = report.context?.location || null;
    this.warnings = report.warnings || [];
    this.summaryLine = isV2Report
      ? buildSummaryLine(nonAmenity.length, this.amenityNames.length)
      : (report.summary || 'This saved report uses the legacy report format.');
  }

  private getRenderableSuggestions(): Suggestion[] {
    const validGroups = new Set(['title', 'description', 'photos', 'amenities']);
    const validValueTypes = new Set(['text', 'photo', 'amenity']);
    const suggestions = this.report?.suggestions;
    if (!Array.isArray(suggestions)) {return [];}

    return suggestions.filter((suggestion): suggestion is Suggestion => {
      if (!suggestion || typeof suggestion !== 'object') {return false;}
      const before = suggestion.before;
      return typeof suggestion.id === 'string' &&
        validGroups.has(suggestion.group) &&
        !!before &&
        typeof before === 'object' &&
        validValueTypes.has(before.type);
    });
  }

  async generate(suggestion: Suggestion): Promise<void> {
    if (this.historyMode) {return;}
    const listingId = this.report?.listing?.listingId;
    if (!listingId) {return;}
    this.genError = { ...this.genError, [suggestion.id]: '' };
    this.cdr.markForCheck();
    try {
      const data = await this.listingOptimizerService.generateSuggestion(listingId, suggestion.id);
      suggestion.after = data.after;
      suggestion.afterStatus = 'ready';
      if (suggestion.after?.type === 'photo') {
        suggestion.after.url = this.listingOptimizerService.absoluteUrl(suggestion.after.url);
        if (suggestion.after.thumbnailUrl) {
          suggestion.after.thumbnailUrl = this.listingOptimizerService.absoluteUrl(suggestion.after.thumbnailUrl);
        }
      }
      delete this.genError[suggestion.id];
      this.buildViewModel();
    } catch (err: any) {
      const status = err?.status;
      if (status === 404) {
        this.reportExpired.emit();
      } else if (status === 503) {
        this.gen503 = { ...this.gen503, [suggestion.id]: true };
        this.genError = { ...this.genError, [suggestion.id]: 'This feature is temporarily offline.' };
        this.buildViewModel();
      } else if (status === 400) {
        this.genError = { ...this.genError, [suggestion.id]: 'Nothing to generate for this item.' };
      } else {
        this.genError = { ...this.genError, [suggestion.id]: 'Generation failed. Please try again.' };
      }
    }
    this.cdr.markForCheck();
  }

  copy = (text: string): void => {
    this.util.copyToClipBoard(text);
  };

  download = async (url: string, filename: string): Promise<void> => {
    const absoluteUrl = this.listingOptimizerService.absoluteUrl(url);
    if (Capacitor.isNativePlatform()) {
      this.util.openUrl(absoluteUrl);
      return;
    }
    try {
      const response = await fetch(absoluteUrl);
      if (!response.ok) {throw new Error('Download failed');}
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch {
      this.util.openUrl(absoluteUrl);
    }
  };

  haveTidy = async (): Promise<void> => {
    this.util.openUrl('https://www.tidy.com/demo');
  };

  openLightbox = (originalUrl: string, enhancedUrl: string | null, view: 'before' | 'after'): void => {
    this.lightbox = { originalUrl, enhancedUrl, view: enhancedUrl ? view : 'before' };
    this.cdr.markForCheck();
  };

  closeLightbox = (): void => {
    this.lightbox = null;
    this.cdr.markForCheck();
  };

  setLightboxView = (view: 'before' | 'after'): void => {
    if (!this.lightbox) {return;}
    this.lightbox = { ...this.lightbox, view };
    this.cdr.markForCheck();
  };
}
